import { useCallback, useMemo, useState } from "react";

import { useSearchParams } from "react-router-dom";

import collaborator1 from "../../../assets/feature-tour/collaborator-1.webp";
import collaborator2 from "../../../assets/feature-tour/collaborator-2.webp";
import member1 from "../../../assets/feature-tour/member-1.webp";
import member2 from "../../../assets/feature-tour/member-2.webp";
import { useGetAppProperties, useSetAppProperties } from "../../../hooks/useGetAppProperties";
import { useProject } from "../../../hooks/useProject";
import { useTracker } from "../../../hooks/useTracker";
import useTranslation from "../../../hooks/useTranslation";
import { useDeepLinkStateStore } from "../../../stores/deepLinkStateStore";
import { SearchParam } from "../../../types";
import { FeatureTour, type FeatureTourSlideItem } from "../FeatureTour/FeatureTour";

export const ProjectPageFeatureTour = () => {
  const { t, m } = useTranslation();
  const { data: projectData } = useProject();

  const tracker = useTracker();

  const trackSlideDisplay = useCallback(
    (slideIndex: number, featureTourType: string) => {
      tracker.trackProjectPageFeatureTourStepShown({
        featureTourStep: slideIndex,
        featureTourType: featureTourType,
        projectId: projectData?.project.projectId || "",
      });
    },
    [tracker, projectData?.project.projectId]
  );

  const trackFeatureTourCompleted = useCallback(
    (featureTourType: string) => {
      tracker.trackProjectPageFeatureTourCompleted({
        featureTourType: featureTourType,
        projectId: projectData?.project.projectId || "",
      });
    },
    [tracker, projectData?.project.projectId]
  );

  const trackFeatureTourClose = useCallback(
    (featureTourType: string) => {
      tracker.trackProjectPageFeatureTourClose({
        featureTourType: featureTourType,
        projectId: projectData?.project.projectId || "",
      });
    },
    [tracker, projectData?.project.projectId]
  );

  const [searchParams] = useSearchParams();
  const { setIsModalOpen: setIsDeepLinkModalOpen } = useDeepLinkStateStore();

  // Avoid showing the tour if there are intent params in the url (e.g. ?intent=share or ?intent=invite)
  const hasIntentParams = searchParams.get(SearchParam.Intent);
  const [isFeatureTourOpen, setIsFeatureTourOpen] = useState(!hasIntentParams);

  const { data: appPropertiesData, isSuccess: appPropertiesIsSuccess } = useGetAppProperties();
  const { mutateAsync: mutateAppProperties } = useSetAppProperties();

  const showFeatureTourMember = useMemo(() => {
    return (
      projectData?.userRole &&
      projectData.userRole === "owner" &&
      appPropertiesIsSuccess &&
      !appPropertiesData?.web_feature_tour_project_sharing_owner_completed
    );
  }, [projectData?.userRole, appPropertiesData, appPropertiesIsSuccess]);

  const showFeatureTourCollaborator = useMemo(() => {
    return (
      projectData?.userRole &&
      projectData.userRole === "editor" &&
      appPropertiesIsSuccess &&
      !appPropertiesData?.web_feature_tour_project_sharing_collaborator_completed
    );
  }, [projectData?.userRole, appPropertiesData, appPropertiesIsSuccess]);

  const slidesCollaborator: FeatureTourSlideItem[] = [
    {
      img: collaborator1,
      title: t(m.ProjectPageFeatureTour.Collaborator.Slide1Title),
      description: t(m.ProjectPageFeatureTour.Collaborator.Slide1Description),
    },
    {
      img: collaborator2,
      title: t(m.ProjectPageFeatureTour.Collaborator.Slide2Title),
      description: t(m.ProjectPageFeatureTour.Collaborator.Slide2Description),
    },
  ];

  const slidesMember: FeatureTourSlideItem[] = [
    {
      img: member1,
      title: t(m.ProjectPageFeatureTour.Member.Slide1Title),
      description: t(m.ProjectPageFeatureTour.Member.Slide1Description),
    },
    {
      img: member2,
      title: t(m.ProjectPageFeatureTour.Member.Slide2Title),
      description: t(m.ProjectPageFeatureTour.Member.Slide2Description),
    },
  ];

  if (showFeatureTourCollaborator)
    return (
      <FeatureTour
        slides={slidesCollaborator.map((slide, index) => ({
          ...slide,
          onDisplay: () => trackSlideDisplay(index, "team-collaborator"),
        }))}
        isOpen={isFeatureTourOpen}
        onClose={() => {
          mutateAppProperties({
            web_feature_tour_project_sharing_collaborator_completed: true,
          });
          setIsFeatureTourOpen(false);
          trackFeatureTourClose("team-collaborator");
        }}
        onComplete={() => {
          setIsFeatureTourOpen(false);
          setIsDeepLinkModalOpen(true);
          trackFeatureTourCompleted("team-collaborator");
        }}
        completeLabel={t(m.ProjectPageFeatureTour.Collaborator.CompleteLabel)}
        telemetryName="project_page_feature_tour"
      />
    );

  if (showFeatureTourMember)
    return (
      <FeatureTour
        slides={slidesMember.map((slide, index) => ({
          ...slide,
          onDisplay: () => trackSlideDisplay(index, "team-member"),
        }))}
        isOpen={isFeatureTourOpen}
        onClose={() => {
          mutateAppProperties({
            web_feature_tour_project_sharing_owner_completed: true,
          });
          setIsFeatureTourOpen(false);
          trackFeatureTourClose("team-member");
        }}
        onComplete={() => {
          setIsFeatureTourOpen(false);
          trackFeatureTourCompleted("team-member");
        }}
        completeLabel={t(m.ProjectPageFeatureTour.Member.CompleteLabel)}
        telemetryName="project_page_feature_tour"
      />
    );

  return null;
};
